import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { hasAnyLoginRole } from '@/features/auth/roleAccess';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Renders children when the user is authenticated and has a permitted role
 * (Admin, SuperAdmin, SchoolAdmin, or ParentRole). Route-level guards restrict pages by role.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  const hasAllowedRole = hasAnyLoginRole(user?.roles);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f0f4f8]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasAllowedRole) {
    return (
      <Navigate
        to="/forbidden"
        state={{ message: 'You do not have permission to access this dashboard.' }}
        replace
      />
    );
  }

  return <>{children}</>;
}
